// The React-free core of the native bridge (lib/native-ui.ts): finding the
// app's plugins and the share sheet. Split out so the public site bundle
// (site/help-copy.ts on /help/:slug, which also runs inside the app's web view)
// can open the share sheet without pulling in React or sonner. It must not
// import anything (test/frontend/lib/native-bridge.test.ts).
//
// A plugin is used only when BOTH hold: the shell announces it with
// "MektebiNativeUI/1" in the user agent (added by ShellBridgeViewController on
// iOS and capacitor.config.js on Android; 1.0 builds do not have it) AND
// Capacitor reports the plugin as present.

export type PluginName = "Dialog" | "ActionSheet" | "Haptics" | "Share" | "Browser"

type CapacitorGlobal = {
  isNativePlatform(): boolean
  getPlatform(): string
  isPluginAvailable(name: string): boolean
  Plugins: Record<string, any>
}

export function capacitor(): CapacitorGlobal | undefined {
  /* v8 ignore next -- server rendering has no window */
  if (typeof window === "undefined") return undefined
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor
}

/** True when the app is a build that shipped the native UI plugins. */
export function hasNativeUI(plugin: PluginName): boolean {
  const cap = capacitor()
  if (!cap?.isNativePlatform()) return false
  if (!window.navigator.userAgent.includes("MektebiNativeUI/")) return false
  return cap.isPluginAvailable(plugin) && !!cap.Plugins[plugin]
}

export interface ShareContent {
  title?: string
  text?: string
  url?: string
  /** Files go through the Web Share API only (the Share plugin needs native file paths). */
  files?: File[]
}

export type ShareResult = "shared" | "copied" | "cancelled" | "unavailable"

type ShareNavigator = Navigator & { share?: (data: ShareData) => Promise<void>; canShare?: (data: ShareData) => boolean }

/** Can the device share these files (so a "Share" button for a PDF makes sense)? */
export function canShareFiles(files: File[]): boolean {
  const nav = navigator as ShareNavigator
  return typeof nav.canShare === "function" && nav.canShare({ files })
}

/** Is there any share sheet (native app plugin or the Web Share API)? */
export function canShare(): boolean {
  return hasNativeUI("Share") || typeof (navigator as ShareNavigator).share === "function"
}

function cancelled(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null
  return e?.name === "AbortError" || /cancel/i.test(e?.message ?? "")
}

/**
 * Open the system share sheet: the Capacitor Share plugin inside the app, the
 * Web Share API in browsers that have it (files only through this one), and as
 * a last resort copy to the clipboard: the link when there is one, otherwise
 * the text (a guide, an invitation text), so nothing is lost.
 */
export async function shareSheet(content: ShareContent): Promise<ShareResult> {
  const nav = navigator as ShareNavigator
  const { files, ...data } = content
  try {
    if (files?.length && nav.share && nav.canShare?.({ files })) {
      await nav.share({ ...data, files })
      return "shared"
    }
    if (hasNativeUI("Share") && (data.url || data.text)) {
      await capacitor()!.Plugins.Share.share({ ...data, dialogTitle: data.title })
      return "shared"
    }
    if (nav.share && (data.url || data.text) && !files?.length) {
      await nav.share(data)
      return "shared"
    }
  } catch (error) {
    if (cancelled(error)) return "cancelled"
  }
  const fallback = data.url || data.text
  if (!fallback) return "unavailable"
  try {
    await navigator.clipboard.writeText(fallback)
  } catch {
    return "unavailable"
  }
  return "copied"
}
